/**
 * Disclaimers, and the rule for when the skipper must see them again.
 *
 * Pure data and one predicate, so the wording is testable and the UI cannot
 * quietly drift from it. Three separate obligations are being met here and they
 * are worth keeping distinct, because only the first is optional in tone:
 *
 * 1. **Honesty about what the app knows.** Everything in §4.6 and §9 of
 *    docs/ARCHITECTURE.md that a skipper would otherwise have to read the source
 *    to discover: depths are to chart datum with no tide correction, bearings
 *    are true with no magnetic variation, unsurveyed depth is treated as unsafe.
 *    Nothing else in the app is allowed to imply otherwise, so neither is this.
 * 2. **The CHS requirement.** Products derived from CHS data are Value-Added
 *    Reseller products: they do not meet the chart carriage requirements of the
 *    Canada Shipping Act, 2001, and CHS requires a disclaimer saying so. They
 *    also may not carry the CHS crest, the original product name, or anything
 *    else that could be read as making this a CHS product.
 *    https://charts.gc.ca/copyright-droitdauteur/index-eng.html
 * 3. **Ordinary limitation of liability**, this being a personal project given
 *    away with no warranty.
 *
 * Not written by a lawyer. See docs/QUESTIONS.md — the CHS licensing position on
 * redistributing derived tiles publicly is a question for Colin, not something
 * this file can settle.
 */

/**
 * Bumped when the wording changes materially enough that a skipper who accepted
 * the old text should see the new one. Acknowledgement is stored as this number
 * rather than a boolean for exactly that reason.
 */
export const DISCLAIMER_VERSION = 1;

/**
 * The short form, kept on screen permanently rather than shown once.
 *
 * A disclaimer that is only in a dialog is one the skipper saw before they had
 * any reason to care. This is the line that has to survive contact with the
 * chart being useful.
 */
export const NOT_FOR_NAVIGATION = 'Not for navigation';

/**
 * Map attribution. Names CHS as the source of the data — required — while
 * saying plainly that this is not their product, also required.
 */
export const CHART_ATTRIBUTION =
  'Contains data from the Canadian Hydrographic Service '
  + '&copy; His Majesty the King in Right of Canada. '
  + 'Not an official chart. Not for navigation.';

export interface LegalSection {
  heading: string;
  paragraphs: readonly string[];
}

/**
 * The full text, shown on first run and reachable afterwards from
 * Settings → Legal.
 *
 * Ordered by what would actually hurt someone, not by legal convention: what
 * the app gets wrong comes before who is not liable for it.
 */
export const LEGAL_SECTIONS: readonly LegalSection[] = [
  {
    heading: 'This is not an official nautical chart',
    paragraphs: [
      'This application is not produced by, endorsed by, or affiliated with the '
      + 'Canadian Hydrographic Service (CHS) or Fisheries and Oceans Canada.',
      'It does not meet the chart carriage and use requirements of the Canada '
      + 'Shipping Act, 2001 (Navigation Safety Regulations, 2020) or the Arctic '
      + 'Waters Pollution Prevention Act. Official, corrected, up-to-date charts '
      + 'and publications issued by or under the authority of the Canadian '
      + 'Hydrographic Service must be carried and used to meet those '
      + 'requirements and to navigate safely.',
      'Use this as a planning and situational-awareness aid alongside official '
      + 'charts, never as a substitute for them.',
    ],
  },
  {
    heading: 'What this app does not know',
    paragraphs: [
      'Depths and drying heights are shown to chart datum. No tide correction is '
      + 'applied, so the actual depth of water differs from what is shown — by '
      + 'more than sixteen metres in the Bay of Fundy. Depths shown are the '
      + 'conservative case; heights of bridges and cables are not.',
      'All bearings and courses are TRUE. Magnetic variation is not applied. In '
      + 'Nova Scotia a compass course steered from a bearing shown here would be '
      + 'wrong by roughly 17 degrees.',
      'Where the chart records no surveyed depth, this app treats the water as '
      + 'unsafe. Route suggestions will therefore avoid water a chart-reading '
      + 'skipper might well use. The reverse error is never made deliberately, '
      + 'but no automatic route may be followed without checking it yourself.',
      'Charts may be out of date. There is no AIS, no radar, no weather, no '
      + 'notices to mariners, and no live data of any kind.',
      'Position comes from this device’s GPS, with consumer-grade accuracy '
      + 'that degrades badly below deck, alongside steel, and in poor sky view.',
    ],
  },
  {
    heading: 'No warranty',
    paragraphs: [
      'This software and its chart data are provided "as is", without warranty of '
      + 'any kind, express or implied, including but not limited to the '
      + 'warranties of merchantability, fitness for a particular purpose, '
      + 'accuracy, and non-infringement.',
      'To the fullest extent permitted by law, the authors and contributors '
      + 'accept no liability for any claim, damage, loss, injury or other '
      + 'liability, whether in contract, tort or otherwise, arising from or in '
      + 'connection with the software, its chart data, or their use.',
      'The master of a vessel remains responsible for its safe navigation.',
    ],
  },
  {
    heading: 'Chart data',
    paragraphs: [
      'Chart data is derived from Canadian Hydrographic Service electronic '
      + 'navigational charts. The copyright in that data is and remains the '
      + 'property of His Majesty the King in Right of Canada and it may not be '
      + 'sold, licensed, leased, assigned or given to a third party.',
      'The data has been reprocessed for display by this application. Any error '
      + 'or omission introduced by that processing is not attributable to the '
      + 'Canadian Hydrographic Service.',
    ],
  },
] as const;

/**
 * Whether the disclaimer must be shown and acknowledged before the chart is
 * usable.
 *
 * `undefined` — never accepted — and any version older than the current one both
 * require acknowledgement. A stored value from the future (a downgrade) is
 * accepted rather than re-prompted: the skipper has seen a newer version of this
 * text, not an older one.
 */
export function needsAcknowledgement(acknowledgedVersion: unknown): boolean {
  return !(typeof acknowledgedVersion === 'number'
    && Number.isFinite(acknowledgedVersion)
    && acknowledgedVersion >= DISCLAIMER_VERSION);
}
